import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { useToast } from '../hooks/useToast';
import { ToastContainer } from '../components/ToastContainer';
import CampaignModal from '../components/CampaignModal';
import CampaignDetailsModal from '../components/CampaignDetailsModal';
import RecipientListModal from '../components/RecipientListModal';
import ExclusionPatternsModal from '../components/ExclusionPatternsModal';
import ExclusionMatchesModal from '../components/ExclusionMatchesModal';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export default function Shoutout() {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showRecipientModal, setShowRecipientModal] = useState(false);
  const [showExclusionModal, setShowExclusionModal] = useState(false);
  const [showExclusionMatchesModal, setShowExclusionMatchesModal] = useState(false);
  const [selectedExclusionPattern, setSelectedExclusionPattern] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [detailsCampaign, setDetailsCampaign] = useState(null);
  const [newCampaignType, setNewCampaignType] = useState('sms');
  const { toasts, success, error: toastError } = useToast();

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterType) params.set('type', filterType);

      const res = await fetch(`${API_URL}/campaigns?${params.toString()}`, {
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Failed to fetch campaigns');
      const data = await res.json();
      setCampaigns(data);
    } catch (err) {
      toastError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, [filterStatus, filterType]);

  const handleNewCampaign = (type) => {
    setNewCampaignType(type);
    setShowCampaignModal(true);
  };

  const handleCampaignSaved = () => {
    setShowCampaignModal(false);
    fetchCampaigns();
    success('Campaign created');
  };

  const handleViewRecipients = async (campaign) => {
    setSelectedCampaign(campaign);
    setShowRecipientModal(true);
  };

  const handleRecipientSaved = () => {
    setShowRecipientModal(false);
    fetchCampaigns();
    success('Recipients saved');
  };

  const handleViewDetails = (campaign) => {
    setDetailsCampaign(campaign);
    setShowDetailsModal(true);
  };

  const handleTogglePause = async (campaign) => {
    try {
      const newStatus = campaign.status === 'active' ? 'paused' : 'active';
      const res = await fetch(`${API_URL}/campaigns/${campaign.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });

      if (!res.ok) throw new Error('Failed to update campaign');
      fetchCampaigns();
      success(`Campaign ${newStatus === 'paused' ? 'paused' : 'resumed'}`);
    } catch (err) {
      toastError(err.message);
    }
  };

  const handleRefreshStatus = async (campaign) => {
    try {
      const res = await fetch(`${API_URL}/campaigns/${campaign.id}/refresh-status`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Failed to refresh status');
      const data = await res.json();
      fetchCampaigns();
      success(`Refreshed ${data.refreshed || 0} delivery statuses`);
    } catch (err) {
      toastError(err.message);
    }
  };

  const handleDelete = async (campaign) => {
    if (!window.confirm(`Delete campaign "${campaign.name}"? This action cannot be undone.`)) return;

    try {
      const res = await fetch(`${API_URL}/campaigns/${campaign.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Failed to delete campaign');
      fetchCampaigns();
      success('Campaign deleted');
    } catch (err) {
      toastError(err.message);
    }
  };

  const handleManualExecute = async (campaign) => {
    try {
      const res = await fetch(`${API_URL}/campaigns/${campaign.id}/execute`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Failed to execute campaign');
      const data = await res.json();
      fetchCampaigns();
      if (data.skipped) {
        success(`Skipped: ${data.reason}`);
      } else {
        success(`Sent ${data.sent || 0}, Failed ${data.failed || 0}`);
      }
    } catch (err) {
      toastError(err.message);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      scheduled: 'bg-blue-100 text-blue-800',
      active: 'bg-green-100 text-green-800',
      paused: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-gray-100 text-gray-800',
      failed: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status] || styles.scheduled}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const isSms = type === 'sms';
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
        isSms ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800'
      }`}>
        {isSms ? 'SMS' : 'Voice'}
      </span>
    );
  };

  return (
    <div className="p-6">
      <ToastContainer toasts={toasts} />

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Shoutout Campaigns</h1>
        <p className="text-sm text-gray-500 mt-1">
          Send bulk SMS messages or voice calls to your customers
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 mb-6">
        <button
          onClick={() => handleNewCampaign('sms')}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
        >
          New SMS Campaign
        </button>
        <button
          onClick={() => handleNewCampaign('voice')}
          className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium"
        >
          New Voice Campaign
        </button>
        <button
          onClick={() => setShowExclusionModal(true)}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
        >
          Exclusion Patterns
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-6">
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">All Statuses</option>
          <option value="scheduled">Scheduled</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
        </select>

        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">All Types</option>
          <option value="sms">SMS</option>
          <option value="voice">Voice</option>
        </select>

        <button
          onClick={fetchCampaigns}
          className="px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
        >
          Refresh
        </button>
      </div>

      {/* Campaign List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : campaigns.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">No campaigns found. Create one to get started.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Name</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Date Range</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Scheduled</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Delivered</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Failed</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Retried</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((campaign) => (
                <tr key={campaign.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleViewDetails(campaign)}
                      className="font-medium text-indigo-600 hover:text-indigo-800 hover:underline text-left"
                    >
                      {campaign.name}
                    </button>
                    <div className="text-xs text-gray-500">
                      Recipients: {campaign.total_recipients || 0}
                    </div>
                  </td>
                  <td className="px-4 py-3">{getTypeBadge(campaign.type)}</td>
                  <td className="px-4 py-3">{getStatusBadge(campaign.status)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {new Date(campaign.start_date).toLocaleDateString()} - {new Date(campaign.end_date).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-center text-sm text-gray-600">
                    {campaign.total_scheduled || 0}
                  </td>
                  <td className="px-4 py-3 text-center text-sm font-medium text-green-600">
                    {campaign.total_delivered || 0}
                  </td>
                  <td className="px-4 py-3 text-center text-sm font-medium text-red-600">
                    {campaign.total_failed || 0}
                  </td>
                  <td className="px-4 py-3 text-center text-sm text-gray-600">
                    {campaign.total_retried || 0}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 flex-wrap">
                      <button
                        onClick={() => handleViewDetails(campaign)}
                        className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                      >
                        Details
                      </button>
                      {campaign.status === 'scheduled' && (
                        <button
                          onClick={() => handleViewRecipients(campaign)}
                          className="px-2 py-1 text-xs bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200 transition-colors"
                        >
                          Recipients
                        </button>
                      )}
                      {(campaign.status === 'active' || campaign.status === 'paused') && (
                        <button
                          onClick={() => handleTogglePause(campaign)}
                          className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 transition-colors"
                        >
                          {campaign.status === 'active' ? 'Pause' : 'Resume'}
                        </button>
                      )}
                      <button
                        onClick={() => handleRefreshStatus(campaign)}
                        className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                      >
                        Refresh
                      </button>
                      {campaign.status === 'active' && (
                        <button
                          onClick={() => handleManualExecute(campaign)}
                          className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                        >
                          Execute
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(campaign)}
                        className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Campaign Modal */}
      {showCampaignModal && (
        <CampaignModal
          type={newCampaignType}
          onClose={() => setShowCampaignModal(false)}
          onSave={handleCampaignSaved}
        />
      )}

      {/* Recipient List Modal */}
      {showRecipientModal && selectedCampaign && (
        <RecipientListModal
          campaignId={selectedCampaign.id}
          campaignType={selectedCampaign.type}
          onClose={() => {
            setShowRecipientModal(false);
            setSelectedCampaign(null);
          }}
          onSave={handleRecipientSaved}
        />
      )}

      {/* Exclusion Patterns Modal */}
      {showExclusionModal && (
        <ExclusionPatternsModal
          onClose={() => setShowExclusionModal(false)}
          onSave={() => setShowExclusionModal(false)}
          onQuery={(pattern) => {
            setSelectedExclusionPattern(pattern);
            setShowExclusionMatchesModal(true);
          }}
        />
      )}

      {/* Exclusion Matches Modal */}
      {showExclusionMatchesModal && selectedExclusionPattern && (
        <ExclusionMatchesModal
          pattern={selectedExclusionPattern}
          onClose={() => {
            setShowExclusionMatchesModal(false);
            setSelectedExclusionPattern(null);
          }}
        />
      )}

      {/* Campaign Details Modal */}
      {showDetailsModal && detailsCampaign && (
        <CampaignDetailsModal
          campaignId={detailsCampaign.id}
          onClose={() => {
            setShowDetailsModal(false);
            setDetailsCampaign(null);
          }}
        />
      )}
    </div>
  );
}
